import cx from 'classnames'
import { useEffect, useState, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface ProgressBarProps extends DefaultProps {
  progressPercentage?: number
  showAnimation?: boolean
  previousPercentage?: number
  progressColor?: string
}

export const ProgressBar: FC<ProgressBarProps> = ({
  progressPercentage = 0,
  previousPercentage = 0,
  progressColor = 'bg-olive-300',
  showAnimation = false,
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
}) => {
  /**
   * Converts decimal values to percentages and removes decimal places
   * If value is between 0 and 1 (inclusive), multiply by 100 and remove decimals
   * Otherwise, return the value as is (truncated to remove decimals)
   */
  const normalizeProgressValue = (value: number): number => {
    if (value > 0 && value <= 1) {
      return Math.floor(value * 100)
    }
    return Math.floor(value)
  }

  const normalizedProgressPercentage = normalizeProgressValue(progressPercentage)
  const normalizedPreviousPercentage = normalizeProgressValue(previousPercentage)

  const [progress, setProgress] = useState<number>(
    showAnimation ? normalizedPreviousPercentage : normalizedProgressPercentage,
  )

  useEffect(() => {
    if (normalizedProgressPercentage > 100) {
      throw new Error('Progress percentage must be between 0 and 100')
    }
    let interval

    // If the progress is not the same as the normalized progress percentage, set the progress to the normalized progress percentage
    if (progress !== normalizedProgressPercentage && showAnimation) {
      // Set the progress to the normalized progress percentage, with a delay of 500ms
      setTimeout(() => {
        setProgress(normalizedProgressPercentage)
      }, 500)
      return () => {
        // Cleanup
        clearTimeout(interval)
      }
    }
  }, [progress, normalizedProgressPercentage, showAnimation])

  const baseClass = cx('flex flex-row h-2 rounded-full overflow-hidden bg-gray-100 text-xs w-full')
  const progressBarClass = cx(`${progressColor} transition-all duration-500 ease-out`)
  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className={finalClasses}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div style={{ width: `${progress.toString()}%` }} className={progressBarClass} />
    </div>
  )
}
